import { useState, useEffect, useCallback } from 'react';
import api from '../api';
import { useLang } from '../i18n/LanguageContext';

const fmtDate = ts => ts ? new Date(ts).toLocaleDateString('uz-UZ', { day: '2-digit', month: '2-digit', year: '2-digit' }) : '—';
const daysSince = ts => ts ? Math.floor((Date.now() - new Date(ts)) / 86400000) : null;

const SEG_TKEYS = {
  '':                   'crmSegAll',
  active_7d:            'crmSeg7d',
  inactive_14d:         'crmSeg14dMissed',
  inactive_30d:         'crmSeg30dMissed',
  high_balance:         'crmSegHighBalance',
  birthday_this_month:  'crmSegBirthdayMonth',
  birthday_today:       'crmSegBirthdayToday',
};

const SEGMENT_TPL_KEYS = {
  birthday_today:      { titleKey: 'crmTplBirthdayTodayTitle', bodyKey: 'crmTplBirthdayTodayBody' },
  inactive_14d:        { titleKey: 'crmTplInactive14dTitle',   bodyKey: 'crmTplInactive14dBody' },
  inactive_30d:        { titleKey: 'crmTplInactive30dTitle',   bodyKey: 'crmTplInactive30dBody' },
  birthday_this_month: { titleKey: 'crmTplBirthdayMonthTitle', bodyKey: 'crmTplBirthdayMonthBody' },
  high_balance:        { titleKey: 'crmTplHighBalanceTitle',   bodyKey: 'crmTplHighBalanceBody' },
  '':                  { titleKey: 'crmTplDefaultTitle',       bodyKey: 'crmTplDefaultBody' },
};

const SEG_OPTIONS = [
  { value: 'birthday_today',     icon: '🎉', labelKey: 'crmSegBirthdayToday',  color: 'var(--pink)'   },
  { value: 'inactive_30d',       icon: '🔴', labelKey: 'crmSeg30dMissed',      color: 'var(--red)'    },
  { value: 'inactive_14d',       icon: '🟡', labelKey: 'crmSeg14dMissed',      color: 'var(--yellow)' },
  { value: 'birthday_this_month',icon: '🎂', labelKey: 'crmSegBirthdayMonth',  color: 'var(--orange)' },
  { value: 'high_balance',       icon: '💰', labelKey: 'crmSegHighBalance',    color: 'var(--purple)' },
  { value: 'active_7d',          icon: '🟢', labelKey: 'crmSeg7d',             color: 'var(--green)'  },
  { value: '',                   icon: '👥', labelKey: 'crmSegAll',            color: 'var(--cyan)'   },
];

function Tab({ active, onClick, label }) {
  return (
    <button onClick={onClick} style={{
      padding: '9px 22px', borderRadius: 10, fontSize: 13, cursor: 'pointer', fontWeight: 600,
      background: active ? 'rgba(0,212,255,0.12)' : 'transparent',
      border: `1px solid ${active ? 'var(--cyan)' : 'var(--border)'}`,
      color: active ? 'var(--cyan)' : 'var(--text2)',
    }}>{label}</button>
  );
}

function MijozlarTab({ filterSegment }) {
  const { t } = useLang();
  const fmt = n => `${Number(n || 0).toLocaleString()} ${t('currency')}`;
  const sourceColor = s => ({ referral: 'var(--green)', organic: 'var(--cyan)', promo: 'var(--purple)' })[s] || 'var(--text3)';

  const [search, setSearch] = useState('');
  const [segment, setSegment] = useState(filterSegment || '');
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ users: [], total: 0, pages: 1 });
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [note, setNote] = useState('');
  const [editBirthday, setEditBirthday] = useState('');
  const [editSource, setEditSource] = useState('');

  useEffect(() => { if (filterSegment) setSegment(filterSegment); }, [filterSegment]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/crm/users?search=${encodeURIComponent(search)}&segment=${segment}&page=${page}`);
      setData(r.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [search, segment, page]);

  useEffect(() => { load(); }, [load]);

  const loadDetail = async user => {
    setSelected(user);
    try {
      const r = await api.get(`/crm/users/${user.id}`);
      setDetail(r.data);
      setEditBirthday(r.data.birthday?.split('T')[0] || '');
      setEditSource(r.data.source || '');
    } catch (e) { console.error(e); }
  };

  const addNote = async () => {
    if (!note.trim()) return;
    try {
      await api.post(`/crm/users/${selected.id}/notes`, { note });
      setNote('');
      loadDetail(selected);
    } catch (e) { alert(t('error')); }
  };

  const deleteNote = async id => {
    try { await api.delete(`/crm/notes/${id}`); loadDetail(selected); }
    catch (e) { alert(t('error')); }
  };

  const saveUser = async () => {
    try {
      await api.put(`/crm/users/${selected.id}`, { birthday: editBirthday, source: editSource });
      alert(t('crmSaved'));
    } catch (e) { alert(t('error')); }
  };

  const COLS = ['crmColName', 'crmColPhone', 'crmColBalance', 'crmColSessions', 'crmColLastVisit', 'crmColSource'];

  return (
    <div style={{ display: 'flex', gap: 20 }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
          <input placeholder={t('crmSearchPlh')} value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
            style={{ flex: 1, minWidth: 180, background: 'var(--card)', border: '1px solid var(--border)',
              color: 'var(--text)', padding: '8px 14px', borderRadius: 9, fontSize: 13, outline: 'none' }} />
          <select value={segment} onChange={e => { setSegment(e.target.value); setPage(1); }}
            style={{ background: 'var(--card)', border: '1px solid var(--border)', color: 'var(--text)',
              padding: '8px 14px', borderRadius: 9, fontSize: 13, outline: 'none' }}>
            <option value="">{t('crmAllSegments')}</option>
            <option value="active_7d">{t('crmSeg7d')}</option>
            <option value="inactive_14d">{t('crmSeg14d')}</option>
            <option value="inactive_30d">{t('crmSeg30d')}</option>
            <option value="high_balance">{t('crmSegHighBalance')}</option>
            <option value="birthday_today">{t('crmSegBirthdayToday')}</option>
            <option value="birthday_this_month">{t('crmSegBirthdayMonth')}</option>
          </select>
        </div>

        <div style={{ background: 'var(--card2)', border: '1px solid var(--border)', borderRadius: 14, overflow: 'hidden' }}>
          {loading ? (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--text3)' }}>{t('loading')}</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: 'var(--card)' }}>
                  {COLS.map(k => (
                    <th key={k} style={{ padding: '10px 14px', textAlign: 'left', color: 'var(--text2)', fontWeight: 600 }}>{t(k)}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.users?.map(u => {
                  const days = daysSince(u.last_session);
                  return (
                    <tr key={u.id} onClick={() => loadDetail(u)}
                      style={{ borderBottom: '1px solid var(--border)', cursor: 'pointer',
                        background: selected?.id === u.id ? 'rgba(0,212,255,0.05)' : 'transparent' }}>
                      <td style={{ padding: '10px 14px', fontWeight: 600 }}>{u.name}</td>
                      <td style={{ padding: '10px 14px', color: 'var(--text2)' }}>{u.phone}</td>
                      <td style={{ padding: '10px 14px', color: 'var(--green)', fontWeight: 700 }}>{fmt(u.balance)}</td>
                      <td style={{ padding: '10px 14px' }}>{u.total_sessions || 0}</td>
                      <td style={{ padding: '10px 14px', color: days > 14 ? 'var(--red)' : days > 7 ? 'var(--orange)' : 'var(--text2)', fontSize: 12 }}>
                        {days !== null ? t('crmDaysAgo').replace('{d}', days) : '—'}
                      </td>
                      <td style={{ padding: '10px 14px' }}>
                        {u.source && (
                          <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 20,
                            color: sourceColor(u.source), background: sourceColor(u.source) + '18',
                            border: `1px solid ${sourceColor(u.source)}40` }}>{u.source}</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
          {data.pages > 1 && (
            <div style={{ padding: '12px 20px', borderTop: '1px solid var(--border)', display: 'flex', gap: 8, justifyContent: 'center' }}>
              {Array.from({ length: data.pages }, (_, i) => i + 1).map(p => (
                <button key={p} onClick={() => setPage(p)} style={{
                  width: 32, height: 32, borderRadius: 8, cursor: 'pointer', fontSize: 12,
                  background: page === p ? 'rgba(0,212,255,0.12)' : 'var(--card)',
                  border: `1px solid ${page === p ? 'var(--cyan)' : 'var(--border)'}`,
                  color: page === p ? 'var(--cyan)' : 'var(--text2)',
                }}>{p}</button>
              ))}
            </div>
          )}
        </div>
      </div>

      {detail && (
        <div style={{ width: 320, flexShrink: 0 }}>
          <div style={{ background: 'var(--card2)', border: '1px solid var(--border)', borderRadius: 14, padding: 20, marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ margin: 0, fontSize: 15 }}>{detail.name}</h3>
              <button onClick={() => { setSelected(null); setDetail(null); }}
                style={{ background: 'transparent', border: 'none', color: 'var(--text3)', cursor: 'pointer', fontSize: 18 }}>×</button>
            </div>
            <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 6 }}>{detail.phone}</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 16 }}>
              {[
                { tKey: 'crmStatBalance',  val: fmt(detail.balance),         color: 'var(--green)' },
                { tKey: 'crmStatPoints',   val: detail.loyalty_points || 0,  color: 'var(--yellow)' },
                { tKey: 'crmStatSessions', val: detail.total_sessions || 0,  color: 'var(--cyan)' },
                { tKey: 'crmStatSpent',    val: fmt(detail.total_spent),     color: 'var(--purple)' },
              ].map(x => (
                <div key={x.tKey} style={{ background: 'var(--card)', borderRadius: 8, padding: '8px 12px' }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: x.color }}>{x.val}</div>
                  <div style={{ fontSize: 11, color: 'var(--text3)' }}>{t(x.tKey)}</div>
                </div>
              ))}
            </div>

            <div style={{ marginBottom: 10 }}>
              <label style={{ display: 'block', fontSize: 11, color: 'var(--text2)', marginBottom: 4 }}>{t('crmBirthday')}</label>
              <input type="date" value={editBirthday} onChange={e => setEditBirthday(e.target.value)}
                style={{ width: '100%', background: 'var(--card)', border: '1px solid var(--border)',
                  color: 'var(--text)', padding: '7px 12px', borderRadius: 8, fontSize: 12, outline: 'none', boxSizing: 'border-box' }} />
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 11, color: 'var(--text2)', marginBottom: 4 }}>{t('crmSource')}</label>
              <select value={editSource} onChange={e => setEditSource(e.target.value)}
                style={{ width: '100%', background: 'var(--card)', border: '1px solid var(--border)',
                  color: 'var(--text)', padding: '7px 12px', borderRadius: 8, fontSize: 12, outline: 'none' }}>
                <option value="">—</option>
                <option value="organic">Organic</option>
                <option value="referral">Referral</option>
                <option value="promo">Promo</option>
              </select>
            </div>
            <button onClick={saveUser} style={{ width: '100%', background: 'rgba(0,212,255,0.1)',
              border: '1px solid var(--cyan)', color: 'var(--cyan)', padding: '9px', borderRadius: 8,
              cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>💾 {t('save')}</button>
          </div>

          {detail.sessions?.length > 0 && (
            <div style={{ background: 'var(--card2)', border: '1px solid var(--border)', borderRadius: 14, overflow: 'hidden', marginBottom: 16 }}>
              <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', fontSize: 11,
                color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: 1 }}>{t('crmLastSessions')}</div>
              {detail.sessions.map(s => (
                <div key={s.id} style={{ padding: '9px 16px', borderBottom: '1px solid var(--border)',
                  display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                  <div>
                    <div style={{ fontWeight: 600 }}>PC #{s.computer_number}</div>
                    <div style={{ color: 'var(--text3)', fontSize: 11 }}>{fmtDate(s.created_at)} · {Math.round((s.duration || 0) / 60)} min</div>
                  </div>
                  <div style={{ color: 'var(--green)', fontWeight: 700 }}>{fmt(s.amount)}</div>
                </div>
              ))}
            </div>
          )}

          <div style={{ background: 'var(--card2)', border: '1px solid var(--border)', borderRadius: 14, overflow: 'hidden' }}>
            <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', fontSize: 11,
              color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: 1 }}>{t('crmNotes')}</div>
            <div style={{ padding: '10px 16px' }}>
              <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
                <input value={note} onChange={e => setNote(e.target.value)} placeholder={t('crmAddNote')}
                  onKeyDown={e => e.key === 'Enter' && addNote()}
                  style={{ flex: 1, background: 'var(--card)', border: '1px solid var(--border)',
                    color: 'var(--text)', padding: '7px 12px', borderRadius: 8, fontSize: 12, outline: 'none' }} />
                <button onClick={addNote} style={{ background: 'rgba(0,212,255,0.1)', border: '1px solid var(--cyan)',
                  color: 'var(--cyan)', padding: '7px 12px', borderRadius: 8, cursor: 'pointer', fontSize: 12 }}>+</button>
              </div>
              {detail.notes?.map(n => (
                <div key={n.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
                  padding: '6px 0', borderBottom: '1px solid var(--border)', gap: 8 }}>
                  <div>
                    <div style={{ fontSize: 12 }}>{n.note}</div>
                    <div style={{ fontSize: 10, color: 'var(--text3)' }}>{fmtDate(n.created_at)}</div>
                  </div>
                  <button onClick={() => deleteNote(n.id)} style={{ background: 'transparent', border: 'none',
                    color: 'var(--red)', cursor: 'pointer', fontSize: 14, padding: 0 }}>×</button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SegmentlarTab({ onSelect }) {
  const { t } = useLang();
  const [segs, setSegs] = useState(null);

  useEffect(() => {
    api.get('/crm/segments').then(r => setSegs(r.data)).catch(() => {});
  }, []);

  const cards = [
    { key: '',                   tKey: 'crmSegAll',           icon: '👥', color: 'var(--cyan)',   count: segs?.all },
    { key: 'active_7d',          tKey: 'crmSeg7d',            icon: '🟢', color: 'var(--green)',  count: segs?.active_7d },
    { key: 'inactive_14d',       tKey: 'crmSeg14dMissed',     icon: '🟡', color: 'var(--yellow)', count: segs?.inactive_14d },
    { key: 'inactive_30d',       tKey: 'crmSeg30dMissed',     icon: '🔴', color: 'var(--red)',    count: segs?.inactive_30d },
    { key: 'high_balance',       tKey: 'crmSegHighBalance',   icon: '💰', color: 'var(--purple)', count: segs?.high_balance },
    { key: 'birthday_this_month',tKey: 'crmSegBirthdayMonth', icon: '🎂', color: 'var(--orange)', count: segs?.birthday_this_month },
    { key: 'birthday_today',     tKey: 'crmSegBirthdayToday', icon: '🎉', color: 'var(--pink)',   count: segs?.birthday_today },
  ];

  return (
    <div>
      <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 20 }}>{t('crmSegHint')}</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
        {cards.map(c => (
          <div key={c.key} onClick={() => onSelect(c.key)}
            style={{ background: 'var(--card2)', border: `1px solid ${c.color}40`, borderRadius: 14,
              padding: '24px 20px', cursor: 'pointer', transition: 'all 0.2s' }}>
            <div style={{ fontSize: 32, marginBottom: 12 }}>{c.icon}</div>
            <div style={{ fontFamily: 'Orbitron, sans-serif', fontSize: 26, fontWeight: 700, color: c.color, marginBottom: 4 }}>
              {segs ? (c.count || 0) : '...'}
            </div>
            <div style={{ fontSize: 13, color: 'var(--text2)' }}>{t(c.tKey)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function KampaniyaTab() {
  const { t } = useLang();
  const fmt = n => `${Number(n || 0).toLocaleString()} ${t('currency')}`;

  const [segment, setSegment]       = useState('birthday_today');
  const [title, setTitle]           = useState('');
  const [body, setBody]             = useState('');
  const [bonusAmt, setBonusAmt]     = useState('');
  const [segs, setSegs]             = useState(null);
  const [segUsers, setSegUsers]     = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [history, setHistory]       = useState([]);
  const [sending, setSending]       = useState(false);
  const [confirm, setConfirm]       = useState(false);
  const [result, setResult]         = useState(null);

  useEffect(() => {
    api.get('/crm/segments').then(r => setSegs(r.data)).catch(() => {});
    api.get('/crm/campaign/history').then(r => setHistory(r.data || [])).catch(() => {});
  }, []);

  // Load users for selected segment
  useEffect(() => {
    setUsersLoading(true);
    setSegUsers([]);
    api.get(`/crm/users?segment=${segment}&page=1`)
      .then(r => setSegUsers(r.data?.users || []))
      .catch(() => {})
      .finally(() => setUsersLoading(false));
  }, [segment]);

  // Auto-fill template when segment changes
  useEffect(() => {
    const tplKeys = SEGMENT_TPL_KEYS[segment] || SEGMENT_TPL_KEYS[''];
    setTitle(t(tplKeys.titleKey));
    setBody(t(tplKeys.bodyKey));
  }, [segment]); // eslint-disable-line react-hooks/exhaustive-deps

  const segCounts = {
    '': segs?.all, active_7d: segs?.active_7d, inactive_14d: segs?.inactive_14d,
    inactive_30d: segs?.inactive_30d, high_balance: segs?.high_balance,
    birthday_this_month: segs?.birthday_this_month, birthday_today: segs?.birthday_today,
  };
  const segCount = segCounts[segment] || 0;

  const previewTitle = title.replace(/{name}/g, 'Sardor').replace(/{club}/g, 'Defuse').replace(/{amount}/g, Number(bonusAmt || 0).toLocaleString());
  const previewBody  = body.replace(/{name}/g, 'Sardor').replace(/{club}/g, 'Defuse').replace(/{amount}/g, Number(bonusAmt || 0).toLocaleString());

  const handleSend = async () => {
    if (!title || !body) return;
    setConfirm(false);
    setSending(true);
    setResult(null);
    try {
      const r = await api.post('/crm/campaign/send', {
        segment, title_template: title, body_template: body, bonus_amount: parseFloat(bonusAmt) || 0,
      });
      setResult(r.data);
      const [h, s] = await Promise.all([
        api.get('/crm/campaign/history'),
        api.get('/crm/segments'),
      ]);
      setHistory(h.data || []);
      setSegs(s.data);
    } catch (e) { alert(e.response?.data?.error || t('error')); }
    finally { setSending(false); }
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '300px 1fr', gap: 20 }}>

      {/* Left: segment picker + user list */}
      <div>
        <div style={{ background: 'var(--card2)', border: '1px solid var(--border)', borderRadius: 14, padding: 14, marginBottom: 14 }}>
          <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 10, textTransform: 'uppercase', letterSpacing: 1 }}>
            {t('crmSelectSegment')}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            {SEG_OPTIONS.map(opt => {
              const active = segment === opt.value;
              return (
                <button key={opt.value} onClick={() => { setSegment(opt.value); setResult(null); }} style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '9px 12px', borderRadius: 8, cursor: 'pointer', textAlign: 'left', width: '100%',
                  background: active ? `${opt.color}18` : 'transparent',
                  border: `1px solid ${active ? opt.color : 'var(--border)'}`,
                  color: active ? opt.color : 'var(--text2)',
                  transition: 'all 0.15s',
                }}>
                  <span style={{ fontSize: 12, fontWeight: active ? 700 : 400 }}>{opt.icon} {t(opt.labelKey)}</span>
                  <span style={{ fontSize: 13, fontWeight: 700 }}>{segs ? (segCounts[opt.value] || 0) : '...'}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Users in segment */}
        <div style={{ background: 'var(--card2)', border: '1px solid var(--border)', borderRadius: 14, overflow: 'hidden' }}>
          <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--border)', fontSize: 11,
            color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: 1 }}>
            {t('crmSegUsersList')} · {segCount}
          </div>
          {usersLoading ? (
            <div style={{ padding: 20, textAlign: 'center', color: 'var(--text3)', fontSize: 12 }}>{t('loading')}</div>
          ) : segUsers.length === 0 ? (
            <div style={{ padding: 20, textAlign: 'center', color: 'var(--text3)', fontSize: 12 }}>{t('crmNoUsers')}</div>
          ) : (
            <div style={{ maxHeight: 340, overflowY: 'auto' }}>
              {segUsers.slice(0, 50).map(u => (
                <div key={u.id} style={{ padding: '9px 14px', borderBottom: '1px solid var(--border)',
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 600 }}>{u.name}</div>
                    <div style={{ fontSize: 11, color: 'var(--text3)' }}>{u.phone}</div>
                  </div>
                  {u.birthday && (
                    <div style={{ fontSize: 10, color: 'var(--orange)' }}>
                      🎂 {new Date(u.birthday).toLocaleDateString('uz-UZ', { day: '2-digit', month: '2-digit' })}
                    </div>
                  )}
                </div>
              ))}
              {segCount > 50 && (
                <div style={{ padding: '8px 14px', fontSize: 11, color: 'var(--text3)', textAlign: 'center', borderTop: '1px solid var(--border)' }}>
                  +{segCount - 50} {t('crmMoreUsers')}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Right: message composer + history */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div style={{ background: 'var(--card2)', border: '1px solid var(--border)', borderRadius: 14, padding: 24 }}>
          <h3 style={{ margin: '0 0 18px', fontSize: 15 }}>{t('crmNewCampaign')}</h3>

          <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', fontSize: 12, color: 'var(--text2)', marginBottom: 5 }}>
              {t('crmBonusLabel')}
            </label>
            <input type="number" value={bonusAmt} onChange={e => setBonusAmt(e.target.value)}
              placeholder={t('crmBonusPlh')}
              style={{ width: '100%', background: 'var(--card)', border: '1px solid var(--border)',
                color: 'var(--text)', padding: '9px 13px', borderRadius: 9, fontSize: 14, outline: 'none',
                boxSizing: 'border-box', fontWeight: bonusAmt ? 700 : 400 }} />
            {bonusAmt > 0 && segCount > 0 && (
              <div style={{ marginTop: 4, fontSize: 11, color: 'var(--orange)' }}>
                {t('crmBonusTotalCalc')
                  .replace('{total}', `${(Number(bonusAmt) * segCount).toLocaleString()} ${t('currency')}`)
                  .replace('{n}', segCount)
                  .replace('{amt}', `${Number(bonusAmt).toLocaleString()} ${t('currency')}`)}
              </div>
            )}
          </div>

          <div style={{ marginBottom: 10 }}>
            <label style={{ display: 'block', fontSize: 12, color: 'var(--text2)', marginBottom: 5 }}>
              {t('crmTitleLabel')} <span style={{ color: 'var(--text3)' }}>({'{name}'} {'{club}'} {'{amount}'})</span>
            </label>
            <input value={title} onChange={e => setTitle(e.target.value)}
              style={{ width: '100%', background: 'var(--card)', border: '1px solid var(--border)',
                color: 'var(--text)', padding: '9px 13px', borderRadius: 9, fontSize: 13, outline: 'none', boxSizing: 'border-box' }} />
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontSize: 12, color: 'var(--text2)', marginBottom: 5 }}>{t('crmBodyLabel')}</label>
            <textarea value={body} onChange={e => setBody(e.target.value)} rows={4}
              style={{ width: '100%', background: 'var(--card)', border: '1px solid var(--border)',
                color: 'var(--text)', padding: '9px 13px', borderRadius: 9, fontSize: 13, outline: 'none',
                boxSizing: 'border-box', resize: 'vertical', fontFamily: 'inherit' }} />
          </div>

          {/* Preview */}
          <div style={{ background: 'rgba(0,212,255,0.05)', border: '1px solid rgba(0,212,255,0.2)', borderRadius: 10, padding: '12px 14px', marginBottom: 16 }}>
            <div style={{ fontSize: 10, color: 'var(--text3)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 1 }}>
              {t('crmPreviewLabel')}
            </div>
            <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 4 }}>{previewTitle || '—'}</div>
            <div style={{ fontSize: 12, color: 'var(--text2)', lineHeight: 1.5 }}>{previewBody || '—'}</div>
          </div>

          <button
            onClick={() => segCount > 0 ? setConfirm(true) : alert(t('crmNoUsers'))}
            disabled={sending || !title || !body}
            style={{ width: '100%', background: sending ? 'rgba(0,255,136,0.05)' : 'rgba(0,255,136,0.12)',
              border: '1px solid var(--green)', color: 'var(--green)', padding: '13px',
              borderRadius: 9, cursor: segCount > 0 && !sending ? 'pointer' : 'default', fontSize: 14, fontWeight: 700 }}>
            {sending ? t('crmSending') : t('crmSendTo').replace('{n}', segCount)}
          </button>

          {result && (
            <div style={{ marginTop: 12, background: 'rgba(0,255,136,0.08)', border: '1px solid var(--green)', borderRadius: 9, padding: '10px 14px', fontSize: 13 }}>
              {t('crmSentResult').replace('{n}', result.sent_count)}
              {result.total_bonus > 0 && ` ${t('crmSentBonus').replace('{total}', `${result.total_bonus.toLocaleString()} ${t('currency')}`)}`}
            </div>
          )}
        </div>

        {/* History */}
        <div style={{ background: 'var(--card2)', border: '1px solid var(--border)', borderRadius: 14, overflow: 'hidden' }}>
          <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)', fontSize: 11,
            color: 'var(--text2)', letterSpacing: 1.5, textTransform: 'uppercase' }}>{t('crmCampaignHistory')}</div>
          {history.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--text3)' }}>{t('crmNoCampaigns')}</div>
          ) : history.map(h => (
            <div key={h.id} style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <span style={{ fontSize: 13, fontWeight: 600 }}>{h.title}</span>
                <span style={{ fontSize: 11, color: 'var(--text3)' }}>{fmtDate(h.created_at)}</span>
              </div>
              <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 6 }}>{h.body}</div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 20,
                  background: 'rgba(0,212,255,0.1)', color: 'var(--cyan)', border: '1px solid rgba(0,212,255,0.3)' }}>
                  {SEG_TKEYS[h.segment] ? t(SEG_TKEYS[h.segment]) : h.segment || t('crmHistoryAll')}
                </span>
                <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 20,
                  background: 'rgba(0,255,136,0.1)', color: 'var(--green)', border: '1px solid rgba(0,255,136,0.3)' }}>
                  👤 {h.sent_count} {t('crmPerPerson')}
                </span>
                {Number(h.bonus_amount) > 0 && (
                  <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 20,
                    background: 'rgba(255,159,67,0.1)', color: 'var(--orange)', border: '1px solid rgba(255,159,67,0.3)' }}>
                    🎁 {fmt(h.bonus_amount)}/{t('crmPerPerson')}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Confirm modal */}
      {confirm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 1000,
          display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setConfirm(false)}>
          <div style={{ background: 'var(--card2)', border: '1px solid var(--border)', borderRadius: 18,
            padding: 32, maxWidth: 420, width: '90%' }} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 12px', fontSize: 16 }}>{t('crmConfirmTitle')}</h3>
            <p style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 8 }}>
              {t('crmConfirmMsg').replace('{n}', segCount)}
              {Number(bonusAmt) > 0 && ` ${t('crmConfirmBonusMsg').replace('{bonus}', fmt(bonusAmt))}`}.
            </p>
            {Number(bonusAmt) > 0 && (
              <p style={{ fontSize: 13, color: 'var(--orange)', marginBottom: 8 }}>
                {t('crmConfirmTotalMsg').replace('{total}', fmt(Number(bonusAmt) * segCount))}
              </p>
            )}
            <div style={{ background: 'var(--card)', borderRadius: 10, padding: '12px 14px', marginBottom: 16 }}>
              <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>📱 Sardor</div>
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 4 }}>{previewTitle}</div>
              <div style={{ fontSize: 12, color: 'var(--text2)', lineHeight: 1.5 }}>{previewBody}</div>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setConfirm(false)} style={{ flex: 1, background: 'transparent',
                border: '1px solid var(--border)', color: 'var(--text2)', padding: '10px', borderRadius: 9, cursor: 'pointer' }}>
                {t('cancelShort')}
              </button>
              <button onClick={handleSend} style={{ flex: 2, background: 'rgba(0,255,136,0.12)',
                border: '1px solid var(--green)', color: 'var(--green)', padding: '10px', borderRadius: 9,
                cursor: 'pointer', fontWeight: 700 }}>
                {t('crmConfirmSend')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function CRM() {
  const { t } = useLang();
  const [tab, setTab] = useState('mijozlar');
  const [segFilter, setSegFilter] = useState('');

  const handleSegSelect = seg => {
    setSegFilter(seg);
    setTab('mijozlar');
  };

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, marginBottom: 4 }}>{t('crmTitle')}</h1>
        <div style={{ fontSize: 13, color: 'var(--text2)' }}>{t('crmSubtitle')}</div>
      </div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 28 }}>
        <Tab active={tab === 'mijozlar'}   onClick={() => setTab('mijozlar')}   label={t('crmTabClients')} />
        <Tab active={tab === 'segmentlar'} onClick={() => setTab('segmentlar')} label={t('crmTabSegments')} />
        <Tab active={tab === 'kampaniya'}  onClick={() => setTab('kampaniya')}  label={t('crmTabCampaign')} />
      </div>
      {tab === 'mijozlar'   && <MijozlarTab filterSegment={segFilter} />}
      {tab === 'segmentlar' && <SegmentlarTab onSelect={handleSegSelect} />}
      {tab === 'kampaniya'  && <KampaniyaTab />}
    </div>
  );
}
